import { create } from "zustand";

/**
 * Scope Router Store (v2)
 * 
 * Manages scope categorization, article references, routing paths,
 * and cross-border bonuses.
 */
export interface ScopeRouterStoreState {
  scopeCategory: "scope1" | "scope2" | "scope3";
  articleRef: "A8" | "A9" | "A10";
  supplyChainCount: number;

  // Actions
  setScopeCategory: (category: "scope1" | "scope2" | "scope3") => void;
  setArticleRef: (ref: "A8" | "A9" | "A10") => void;
  setSupplyChainCount: (count: number) => void;

  // Derived outputs
  getRoutingPath: () => string[];
  getLedgerTarget: () => string;
  getCrossBorderBonus: () => number;
  getArticleMismatch: () => boolean;

  // Export
  exportScopeRoute: () => void;
}

// Valid combinations
const VALID_COMBINATIONS: Record<string, string[]> = {
  scope1: ["A8"],
  scope2: ["A8", "A9"],
  scope3: ["A9", "A10"],
};

export const useScopeRouterStore = create<ScopeRouterStoreState>((set, get) => ({
  scopeCategory: "scope1",
  articleRef: "A8",
  supplyChainCount: 0,

  setScopeCategory: (category) => set({ scopeCategory: category }),
  setArticleRef: (ref) => set({ articleRef: ref }),
  setSupplyChainCount: (count) => set({ supplyChainCount: Math.max(0, count) }),

  getRoutingPath: () => {
    const state = get();
    const path = [];
    path.push(`scope:${state.scopeCategory}`);
    path.push(`article:${state.articleRef}`);
    if (state.supplyChainCount > 0) {
      path.push(`supplyChain:${state.supplyChainCount}`);
    }
    path.push("ledger:auditregister");
    return path;
  },

  getLedgerTarget: () => {
    const state = get();
    if (state.scopeCategory === "scope1") return "auditregister-scope1";
    if (state.scopeCategory === "scope2") return "auditregister-scope2";
    return "auditregister-scope3";
  },

  getCrossBorderBonus: () => {
    return get().supplyChainCount * 0.3;
  },

  getArticleMismatch: () => {
    const state = get();
    const valid = VALID_COMBINATIONS[state.scopeCategory] || [];
    return !valid.includes(state.articleRef);
  },

  exportScopeRoute: () => {
    const state = get();
    const report = {
      scopeCategory: state.scopeCategory,
      articleRef: state.articleRef,
      supplyChainCount: state.supplyChainCount,
      routingPath: state.getRoutingPath(),
      ledgerTarget: state.getLedgerTarget(),
      crossBorderBonus: state.getCrossBorderBonus(),
      articleMismatch: state.getArticleMismatch(),
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "scopeRoute.json";
    a.click();
    URL.revokeObjectURL(url);
  },
}));











